import {action, computed, runInAction} from 'mobx';
import {TechseeBaseController} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-controller';
import {PasswordErrorType as ApiPasswordErrorType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {LabelType} from '@techsee/techsee-ui-common/lib/notification-label';
import {IMainLayoutController} from '../../layouts/main-layout/layout.controller';
import {ITsAppUserService} from '../../services/TsAppUserService';
import {ChangeSettingsForm} from './ChangeSettingsForm.model';
import {ChangeSettingsModalState} from './ChangeSettingsModal.state';
import {createFieldModel} from '../../app.bootstrap';
import {PasswordErrorType} from './ChangeSettingsModal.constants';

interface ChangeSettingsModalOptions {
    currentUser: any;
    accountData: any;
    passwordOnly?: boolean;
}

export class ChangeSettingsModalController extends TechseeBaseController {
    private userId: string = '';
    private formModel: ChangeSettingsForm | null = null;
    private resolvePromise: ((result: {firstName: string; lastName: string}) => void) | null = null;
    private rejectPromise: (() => void) | null = null;
    private initialFirstName: string = '';
    private initialLastName: string = '';

    constructor(
        public state: ChangeSettingsModalState,
        private userService: ITsAppUserService,
        private mainLayoutController?: IMainLayoutController
    ) {
        super();
    }

    @action
    open(opts: ChangeSettingsModalOptions): Promise<{firstName: string; lastName: string}> {
        this.state.accountData = opts.accountData;
        this.state.passwordOnly = opts.passwordOnly || false;
        this.userId = opts.currentUser._id;

        const passwordSettings = opts.accountData?.settings?.passwordSettings;
        const allowCustom = opts.accountData?.protectedSettings?.allowCustomPasswordPolicy;

        this.formModel = new ChangeSettingsForm(
            this.translate,
            createFieldModel,
            passwordSettings,
            allowCustom,
            this.state.passwordOnly
        );

        // Set initial values
        if (!this.state.passwordOnly) {
            this.initialFirstName = opts.currentUser.firstName || '';
            this.initialLastName = opts.currentUser.lastName || '';
            this.formModel.fields.firstName?.setValue(this.initialFirstName);
            this.formModel.fields.lastName?.setValue(this.initialLastName);
        } else {
            this.initialFirstName = '';
            this.initialLastName = '';
        }

        // Reset all fields to pristine state
        Object.values(this.formModel.fields).forEach((field) => {
            if (field) {
                field.setFieldError('');
            }
        });

        this.state.isVisible = true;
        this.state.formSubmitted = false;
        this.state.wrongPassword = false;
        this.state.usedPassword = false;

        return new Promise<{firstName: string; lastName: string}>((resolve, reject) => {
            this.resolvePromise = resolve;
            this.rejectPromise = reject;
        });
    }

    @action
    close(result?: {firstName: string; lastName: string}) {
        if (!this.state.isVisible) {
            return;
        }
        this.state.isVisible = false;
        this.state.saving = false;

        if (this.resolvePromise) {
            if (result) {
                this.resolvePromise(result);
            } else {
                // Return original values if no result provided (no changes made)
                const resolvedResult = {
                    firstName: this.initialFirstName,
                    lastName: this.initialLastName
                };
                this.resolvePromise(resolvedResult);
            }
            this.resolvePromise = null;
            this.rejectPromise = null;
        }

        // Clean up form model
        this.formModel = null;
    }

    @action
    dismiss() {
        if (!this.state.isVisible) {
            return;
        }
        this.state.isVisible = false;
        this.state.saving = false;

        if (this.rejectPromise) {
            this.rejectPromise();
            this.resolvePromise = null;
            this.rejectPromise = null;
        }

        this.emit('DISMISSED');
    }

    @action
    setFirstName(value: string) {
        this.formModel?.fields.firstName?.setValue(value);
    }

    @action
    setLastName(value: string) {
        this.formModel?.fields.lastName?.setValue(value);
    }

    @action
    setOldPassword(value: string) {
        this.formModel?.fields.oldPassword?.setValue(value);
        this.state.wrongPassword = false;
        this.state.usedPassword = false;
    }

    @action
    setNewPassword(value: string) {
        const newPasswordField = this.formModel?.fields.newPassword;
        if (!newPasswordField) {
            return;
        }

        newPasswordField.setValue(value);
        newPasswordField.setWasChanged(true);
        this.state.wrongPassword = false;
        this.state.usedPassword = false;

        // Clear confirm password if new password is cleared
        if (!value) {
            this.formModel?.fields.confirmPassword?.setValue('');
            this.formModel?.setFieldError('confirmPassword', '', false);
        } else {
            // Check pattern match with confirm password if it has a value
            const confirmPasswordField = this.formModel?.fields.confirmPassword;
            if (confirmPasswordField && confirmPasswordField.value) {
                if (value !== confirmPasswordField.value) {
                    this.formModel?.setFieldError(
                        'confirmPassword',
                        this.translate('CHANGE_SETTINGS.VIEW.PASSWORD_MISMATCH'),
                        false
                    );
                } else {
                    this.formModel?.setFieldError('confirmPassword', '', false);
                }
            }
        }
    }

    @action
    setConfirmPassword(value: string) {
        const confirmPasswordField = this.formModel?.fields.confirmPassword;
        if (!confirmPasswordField) {
            return;
        }

        confirmPasswordField.setValue(value);
        this.state.wrongPassword = false;
        this.state.usedPassword = false;

        const newPasswordField = this.formModel?.fields.newPassword;
        if (newPasswordField && newPasswordField.value) {
            const newPassword = newPasswordField.value as string;
            if (newPassword !== value) {
                this.formModel?.setFieldError(
                    'confirmPassword',
                    this.translate('CHANGE_SETTINGS.VIEW.PASSWORD_MISMATCH'),
                    false
                );
            } else {
                this.formModel?.setFieldError('confirmPassword', '', false);
            }
        } else {
            this.formModel?.setFieldError('confirmPassword', '', false);
        }
    }

    @action
    toggleShowOldPassword() {
        this.state.showOldPassword = !this.state.showOldPassword;
    }

    @action
    toggleShowNewPassword() {
        this.state.showNewPassword = !this.state.showNewPassword;
    }

    @action
    toggleShowConfirmPassword() {
        this.state.showConfirmPassword = !this.state.showConfirmPassword;
    }

    @action
    async submit() {
        runInAction(() => {
            this.state.formSubmitted = true;
        });

        if (!this.formModel) {
            return;
        }

        const isValid = await this.formModel.validateForm();

        if (!isValid) {
            return;
        }

        // Check if anything actually changed (using computed property)
        if (!this.hasChanges) {
            // Nothing changed, just close the modal (will return original values)
            this.close();
            return;
        }

        const formValues = this.formModel.getFormValues();

        if (this.state.passwordOnly && !formValues.oldPassword) {
            runInAction(() => {
                this.state.wrongPassword = true;
            });
            return;
        }

        runInAction(() => {
            this.state.saving = true;
        });

        try {
            const toUpdate: any = {};

            // Only include firstName/lastName if they have values (not empty strings)
            if (formValues.firstName && formValues.firstName.trim().length > 0) {
                toUpdate.firstName = formValues.firstName;
            }
            if (formValues.lastName && formValues.lastName.trim().length > 0) {
                toUpdate.lastName = formValues.lastName;
            }

            if (formValues.newPassword) {
                toUpdate.oldPassword = formValues.oldPassword;
                toUpdate.newPassword = formValues.newPassword;
            }

            await this.userService.update(this.userId, toUpdate);
            runInAction(() => {
                this.state.saving = false;
            });

            // Show success notification via MainLayoutController if available
            if (this.mainLayoutController) {
                this.mainLayoutController.showNotificationMessage({
                    message: this.translate('CHANGE_SETTINGS.VIEW.CHANGED'),
                    labelType: LabelType.Success
                });
            } else {
                // Emit event for backward compatibility
                this.emit('SETTINGS_CHANGED', {
                    message: this.translate('CHANGE_SETTINGS.VIEW.CHANGED'),
                    type: LabelType.Success
                });
            }

            const result = {
                firstName: toUpdate.firstName,
                lastName: toUpdate.lastName
            };
            this.close(result);
            this.emit('CLOSED');
        } catch (err: any) {
            runInAction(() => {
                this.state.saving = false;

                const errorMessage = err.response?.data?.message || err.data?.message || err.message;

                if (errorMessage === ApiPasswordErrorType.passwordMismatch) {
                    this.state.wrongPassword = true;
                }

                if (errorMessage === ApiPasswordErrorType.usedPassword) {
                    this.state.usedPassword = true;
                }
            });
        }
    }

    @computed
    get data() {
        return (
            this.formModel?.getFormValues() || {
                firstName: '',
                lastName: '',
                oldPassword: '',
                newPassword: '',
                confirmPassword: ''
            }
        );
    }

    @computed
    get hasNewPasswordError(): boolean {
        const newPasswordField = this.formModel?.fields.newPassword;
        if (!newPasswordField) {
            return false;
        }
        // Only show errors if field has been changed/blurred or form has been submitted
        const shouldShowErrors = this.state.formSubmitted || newPasswordField.wasChanged || newPasswordField.wasBlured;
        return (
            shouldShowErrors &&
            newPasswordField.errors.length > 0 &&
            (newPasswordField.value?.toString().length || 0) > 0
        );
    }

    @computed
    get hasConfirmPasswordError(): boolean {
        const confirmPasswordField = this.formModel?.fields.confirmPassword;
        return (
            (confirmPasswordField?.errors.length || 0) > 0 && (confirmPasswordField?.value?.toString().length || 0) > 0
        );
    }

    @computed
    get isOldPasswordValid(): boolean {
        const oldPasswordField = this.formModel?.fields.oldPassword;
        return (oldPasswordField?.value?.toString().length || 0) > 0 && !this.state.wrongPassword;
    }

    @computed
    get newPasswordErrors(): Record<string, boolean> {
        const errors: Record<string, boolean> = {};
        const newPasswordField = this.formModel?.fields.newPassword;
        if (!newPasswordField) {
            return errors;
        }

        // Only show errors if user has interacted with the new password field or form was submitted
        const newPasswordTouched = newPasswordField.wasChanged || newPasswordField.wasBlured;
        const shouldShowErrors = this.state.formSubmitted || newPasswordTouched;

        if (shouldShowErrors && newPasswordField.errors.length > 0) {
            const errorMessages = newPasswordField.errors.join(' ').toLowerCase();

            if (errorMessages.includes('uppercase')) {
                errors[PasswordErrorType.Uppercase] = true;
            }
            if (errorMessages.includes('lowercase')) {
                errors[PasswordErrorType.Lowercase] = true;
            }
            if (errorMessages.includes('digit')) {
                errors[PasswordErrorType.Digit] = true;
            }
            if (errorMessages.includes('special')) {
                errors[PasswordErrorType.Special] = true;
            }
            if (errorMessages.includes('length') || errorMessages.includes('minimum')) {
                errors[PasswordErrorType.Length] = true;
            }
            if (errorMessages.includes('pattern') || errorMessages.includes('password')) {
                errors[PasswordErrorType.Default] = true;
            }

            // Required error only shows on submit (like old dialog)
            if (this.state.formSubmitted && errorMessages.includes('required')) {
                errors.required = true;
            }
        }

        return errors;
    }

    @computed
    get confirmPasswordErrors(): Record<string, boolean> {
        const errors: Record<string, boolean> = {};
        const confirmPasswordField = this.formModel?.fields.confirmPassword;
        if (confirmPasswordField && confirmPasswordField.errors.length > 0) {
            const errorMessages = confirmPasswordField.errors.join(' ').toLowerCase();
            if (errorMessages.includes('required')) {
                errors.required = true;
            }
            if (errorMessages.includes('mismatch') || errorMessages.includes('match')) {
                errors.pattern = true;
            }
        }
        return errors;
    }

    @computed
    get firstNewPasswordError(): string | null {
        const errors = this.newPasswordErrors;
        if (errors[PasswordErrorType.Uppercase]) {
            return PasswordErrorType.Uppercase;
        }
        if (errors[PasswordErrorType.Lowercase]) {
            return PasswordErrorType.Lowercase;
        }
        if (errors[PasswordErrorType.Digit]) {
            return PasswordErrorType.Digit;
        }
        if (errors[PasswordErrorType.Special]) {
            return PasswordErrorType.Special;
        }
        if (errors[PasswordErrorType.Default]) {
            return PasswordErrorType.Default;
        }
        if (errors[PasswordErrorType.Length]) {
            return PasswordErrorType.Length;
        }
        return null;
    }

    @computed
    get hasChanges(): boolean {
        if (!this.formModel) {
            return false;
        }

        // Access observable fields directly to make this computed reactive
        const firstName = (this.formModel.fields.firstName?.value as string) || '';
        const lastName = (this.formModel.fields.lastName?.value as string) || '';
        const newPassword = (this.formModel.fields.newPassword?.value as string) || '';

        return newPassword.length > 0 || firstName !== this.initialFirstName || lastName !== this.initialLastName;
    }
}
